import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OrderLookupPanel from '../components/OrderLookupPanel';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  CheckCircleOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { compactParams, platformCurrencyAPI } from '../services/api';

const { Text } = Typography;
const { TextArea } = Input;

const firstDefined = (...values) => values.find((value) => (
  value !== undefined && value !== null && value !== ''
));

const normalizeGrant = (input = {}) => {
  const record = input.grant || input.order || input.manualGrant || input.manual_grant || input;
  const wallet = record.wallet || input.wallet || {};

  return {
    ...record,
    id: firstDefined(record.id, record.log_id, record.order_id, record.order_no, record.source_id),
    order_no: firstDefined(record.order_no, record.manual_order_no, record.source_id, record.payment_order_no),
    account_id: firstDefined(record.account_id, record.user_id, input.account_id, input.user_id),
    account_name: firstDefined(
      record.account_name,
      record.user_name,
      record.target_account_name,
      input.account_name,
      input.user_name
    ),
    amount: firstDefined(record.amount, record.change_amount, record.total_amount, input.amount, 0),
    balance_before: firstDefined(
      record.balance_before,
      record.previous_balance,
      record.old_balance,
      wallet.balance_before,
      input.balance_before
    ),
    balance_after: firstDefined(
      record.balance_after,
      record.current_balance,
      record.new_balance,
      wallet.balance_after,
      input.balance_after
    ),
    remark: firstDefined(record.remark, record.note, input.remark, ''),
    operator_name: firstDefined(
      record.operator_name,
      record.operator,
      record.created_by_name,
      record.created_by,
      record.admin_name,
      record.operator_username,
      input.operator_name,
      '-'
    ),
    create_time: firstDefined(record.create_time, record.created_at, record.created_time, record.finish_time)
  };
};

const formatCoin = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('zh-CN');
};

const createRequestId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `grant_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

function PlatformCoinGrantManagement() {
  const [grantForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [grants, setGrants] = useState([]);
  const [lastGrant, setLastGrant] = useState(null);
  const [pendingRequestId, setPendingRequestId] = useState('');
  const [unifiedLookupActive, setUnifiedLookupActive] = useState(false);
  const keyword = '';
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const loadGrants = useCallback(async ({
    page = 1,
    limit = 20,
    query = ''
  } = {}) => {
    setLoading(true);
    try {
      const response = await platformCurrencyAPI.getManualGrants(compactParams({
        page,
        limit,
        keyword: query
      }));
      const payload = response.data?.data || {};
      const rows = payload.list
        || payload.grants
        || payload.manual_grants
        || payload.orders
        || payload.records
        || payload.items
        || payload.rows
        || [];
      const total = Number(payload.pagination?.total ?? payload.meta?.total ?? payload.total ?? rows.length) || 0;

      setGrants(Array.isArray(rows) ? rows.map(normalizeGrant) : []);
      setPagination((current) => ({ ...current, current: page, pageSize: limit, total }));
    } catch (error) {
      message.error(error.response?.data?.message || '加载平台币补发订单失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGrants({
      page: pagination.current,
      limit: pagination.pageSize,
      query: keyword
    });
  }, [keyword, loadGrants, pagination.current, pagination.pageSize]);

  const showSuccess = (grant) => {
    Modal.success({
      title: '平台币补发成功',
      icon: <CheckCircleOutlined />,
      width: 560,
      content: (
        <Descriptions column={1} bordered size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="补发订单号">
            <Text code copyable>{grant.order_no || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="玩家账号">{grant.account_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="补发金额">{formatCoin(grant.amount)} 平台币</Descriptions.Item>
          <Descriptions.Item label="补发前余额">{formatCoin(grant.balance_before)}</Descriptions.Item>
          <Descriptions.Item label="补发后余额">{formatCoin(grant.balance_after)}</Descriptions.Item>
        </Descriptions>
      )
    });
  };

  const submitGrant = async (values) => {
    const request = {
      account_name: values.account_name.trim(),
      amount: Number(values.amount),
      remark: values.remark.trim(),
      request_id: values.request_id || createRequestId()
    };

    setSubmitting(true);
    try {
      const response = await platformCurrencyAPI.createManualGrant(request);
      const grant = normalizeGrant({
        ...(response.data?.data || {}),
        account_name: firstDefined(response.data?.data?.account_name, request.account_name),
        amount: firstDefined(response.data?.data?.amount, request.amount),
        remark: firstDefined(response.data?.data?.remark, request.remark)
      });

      setLastGrant(grant);
      setPendingRequestId('');
      grantForm.resetFields();
      message.success(response.data?.message || '平台币补发成功');
      showSuccess(grant);
      await loadGrants({ page: 1, limit: pagination.pageSize, query: keyword });
    } catch (error) {
      message.error(error.response?.data?.message || '平台币补发失败');
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleGrantSubmit = (values) => {
    const requestId = pendingRequestId || createRequestId();
    setPendingRequestId(requestId);
    const request = {
      account_name: values.account_name.trim(),
      amount: Number(values.amount),
      remark: values.remark.trim(),
      request_id: requestId
    };

    Modal.confirm({
      title: '确认补发平台币？',
      width: 560,
      okText: '确认补发',
      cancelText: '返回检查',
      okButtonProps: { danger: true },
      content: (
        <div style={{ marginTop: 16 }}>
          <Alert
            type="warning"
            showIcon
            message="提交后将立即修改玩家平台币余额，并计入累计充值。"
            description="该记录只进入人工补发订单，不计入商城销售流水和代理流水。"
            style={{ marginBottom: 16 }}
          />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="玩家账号">{request.account_name}</Descriptions.Item>
            <Descriptions.Item label="补发金额">{formatCoin(request.amount)} 平台币</Descriptions.Item>
            <Descriptions.Item label="备注">{request.remark}</Descriptions.Item>
          </Descriptions>
        </div>
      ),
      onOk: () => submitGrant(request)
    });
  };

  const columns = useMemo(() => ([
    {
      title: '补发订单号',
      dataIndex: 'order_no',
      width: 220,
      render: (value) => value ? <Text code copyable>{value}</Text> : '-'
    },
    {
      title: '玩家账号',
      dataIndex: 'account_name',
      width: 180,
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value || '-'}</Text>
          {record.account_id ? <Text type="secondary">账号 ID：{record.account_id}</Text> : null}
        </Space>
      )
    },
    {
      title: '补发金额',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (value) => <Text strong style={{ color: '#ea580c' }}>+{formatCoin(value)}</Text>
    },
    {
      title: '余额变化',
      key: 'balance_change',
      width: 190,
      render: (_, record) => (
        <Space size={6}>
          <Text>{formatCoin(record.balance_before)}</Text>
          <Text type="secondary">→</Text>
          <Text strong style={{ color: '#16a34a' }}>{formatCoin(record.balance_after)}</Text>
        </Space>
      )
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 260,
      ellipsis: true,
      render: (value) => value
        ? <Tooltip title={value}><span>{value}</span></Tooltip>
        : <Text type="secondary">-</Text>
    },
    {
      title: '操作人',
      dataIndex: 'operator_name',
      width: 130,
      render: (value) => <Tag color="blue">{value || '-'}</Tag>
    },
    {
      title: '补发时间',
      dataIndex: 'create_time',
      width: 180,
      render: formatDateTime
    }
  ]), []);

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">平台币补发</div>
          <div className="page-subtitle">按玩家账号人工补发平台币，并保留独立订单和操作备注。</div>
        </div>
        <div className="page-header-actions">
          <Tag color="purple" icon={<WalletOutlined />}>人工补发</Tag>
          <Tag color="blue">独立流水</Tag>
        </div>
      </div>

      <OrderLookupPanel onActiveChange={setUnifiedLookupActive} />
      {!unifiedLookupActive && <><Alert
        type="info"
        showIcon
        message="补发口径说明"
        description="补发金额会增加玩家的平台币余额并计入累计充值，同时保留独立补发订单供查询；不会计入商城销售额、商城经营统计或代理流水。"
        style={{ marginBottom: 16 }}
      />

      <Card
        className="section-card"
        title={<Space><SendOutlined />新建平台币补发</Space>}
        extra={<Text type="secondary">提交前会再次确认账号、金额和备注</Text>}
      >
        <Form
          form={grantForm}
          layout="vertical"
          onFinish={handleGrantSubmit}
          onValuesChange={() => setPendingRequestId('')}
          requiredMark="optional"
        >
          <Row gutter={16} align="bottom">
            <Col xs={24} md={8}>
              <Form.Item
                name="account_name"
                label="玩家账号"
                rules={[
                  { required: true, message: '请输入玩家账号' },
                  {
                    validator: (_, value) => (value && value.trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error('玩家账号不能为空')))
                  }
                ]}
              >
                <Input
                  allowClear
                  maxLength={64}
                  prefix={<SearchOutlined />}
                  placeholder="请输入完整玩家账号"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="amount"
                label="补发金额"
                rules={[
                  { required: true, message: '请输入补发金额' },
                  { type: 'number', min: 0.01, message: '补发金额必须大于 0' },
                  { type: 'number', max: 1000000, message: '单次补发金额不能超过 1000000' }
                ]}
              >
                <InputNumber
                  min={0.01}
                  max={1000000}
                  precision={2}
                  step={1}
                  addonAfter="平台币"
                  placeholder="0.00"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="remark"
                label="补发备注"
                rules={[
                  { required: true, message: '请填写补发原因或说明' },
                  { max: 255, message: '备注不能超过 255 个字符' },
                  {
                    validator: (_, value) => (value && value.trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error('备注不能为空')))
                  }
                ]}
              >
                <TextArea
                  rows={2}
                  maxLength={255}
                  showCount
                  placeholder="请填写补发原因、工单号或其他说明"
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitting}
              onClick={() => grantForm.submit()}
            >
              核对并补发
            </Button>
          </div>
        </Form>
      </Card>

      {lastGrant ? (
        <Card className="section-card" style={{ marginTop: 16 }}>
          <Alert
            type="success"
            showIcon
            message="最近一笔平台币已补发成功"
            description={(
              <Space wrap size="large" style={{ marginTop: 8 }}>
                <span>订单号：<Text code copyable>{lastGrant.order_no || '-'}</Text></span>
                <span>账号：<Text strong>{lastGrant.account_name || '-'}</Text></span>
                <span>余额：{formatCoin(lastGrant.balance_before)} → <Text strong>{formatCoin(lastGrant.balance_after)}</Text></span>
              </Space>
            )}
          />
        </Card>
      ) : null}

      <Card
        className="table-card"
        title={<Space><HistoryOutlined />管理员补发审计记录（查单请使用上方统一入口）</Space>}
        extra={<Text type="secondary">共 {pagination.total || 0} 条</Text>}
        style={{ marginTop: 16 }}
      >
        <div className="filter-bar-row" style={{ marginBottom: 16 }}>
          <Text type="secondary">此处保留管理员补发审计；商城及新旧客服订单请在上方全区查单。</Text>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadGrants({
              page: pagination.current,
              limit: pagination.pageSize,
              query: keyword
            })}
          >
            刷新
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={grants}
          rowKey={(record, index) => record.id || record.order_no || `${record.account_name}-${record.create_time}-${index}`}
          loading={loading}
          scroll={{ x: 1290 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination((current) => ({
                ...current,
                current: pageSize !== current.pageSize ? 1 : page,
                pageSize
              }));
            }
          }}
          locale={{ emptyText: '暂无平台币补发订单' }}
        />
      </Card>
      </>}
    </div>
  );
}

export default PlatformCoinGrantManagement;
